import React, { type KeyboardEventHandler } from 'react';

import Minus from '@publicImages/svg/minus.svg?static';
import Plus from '@publicImages/svg/plus.svg?static';
import Sailors from '@publicImages/svg/sailors.svg?static';

import DisabledSailorPopup from '@/components/DisabledSailorPopup';
import { useDisabledSailorModal } from '@/components/DisabledSailorPopup/hooks/useDisabledSailorModal';
import Button from '@/components/elements/Button';
import * as Dialog from '@/components/elements/Dialog';
import { ThreeDotsLoaderContent } from '@/components/elements/loaders/ThreeDotsContent';
import UIResource from '@/components/UIResource';
import UIResourceImage from '@/components/UIResource/Image';
import { setFilter } from '@/ducks/filters/setters';
import { selectIsMnvv } from '@/ducks/mnvv/selectors';
import { toCategoryPage } from '@/ducks/voyageInfo/category/navigation';
import {
  selectIsCabinCategoriesError,
  selectIsCabinCategoriesIsLoading,
  selectIsCabinCategoriesLoaded,
} from '@/reducers/voyagePlanner/chooseCabin/cabinCategories/selectors';
import { useAppSelector } from '@/store';

import styles from './VerificationDialog.module.scss';

type Props = {
  accessible: boolean;
  sailors: number;
};

const VerificationModal = ({ accessible, sailors }: Props) => {
  const isCabinCategoriesIsLoading = useAppSelector(selectIsCabinCategoriesIsLoading);
  const isCabinCategoriesIsLoaded = useAppSelector(selectIsCabinCategoriesLoaded);
  const hasCabinCategoriesError = useAppSelector((state) => !!selectIsCabinCategoriesError(state)?.type);
  const isMNVV = useAppSelector(selectIsMnvv);

  const {
    isDisabledSailorModalOpen,
    onDisabledSailorModalClick,
    onSailorServiceBtnClick,
    setIsDisabledSailorModalOpen,
  } = useDisabledSailorModal();

  const isViewCabinsButtonLoading = isCabinCategoriesIsLoading || !isCabinCategoriesIsLoaded;
  const isViewCabinsButtonDisabled = isViewCabinsButtonLoading || hasCabinCategoriesError;

  const onSailorsDecrement = () => {
    if (isMNVV && sailors <= 2) {
      setIsDisabledSailorModalOpen(true);
      return;
    }

    if (sailors <= 1) {
      return;
    }

    setFilter.sailors(sailors - 1);
  };
  const onSailorsIncrement = () => {
    if (sailors >= 4) return;

    setFilter.sailors(sailors + 1);
  };
  const onIsAccessibleToggle = () => {
    setFilter.accessible(!accessible);
  };
  const onAccessibleToggleKeydown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      onIsAccessibleToggle();
    }
  };

  const handleSubmit: React.FormEventHandler = (event) => {
    event.preventDefault();

    toCategoryPage();
  };

  return (
    <Dialog.Content
      className={styles.wrapper}
      closeSlot={<Dialog.CloseButton aria-label="Close travel party dialog" className={styles.close} />}
      containerClassName={styles.content}
      overlayClassName={styles.overlay}
      role="presentation"
    >
      {!isDisabledSailorModalOpen && (
        <UIResourceImage className={styles.image} resourceId="CabinDescriptor.travelParty.image" role="presentation" />
      )}

      <Dialog.Title className={styles.title}>
        <UIResource id="CabinDescriptor.travelParty.heading" />
      </Dialog.Title>
      <Dialog.Description className={styles.description}>
        <UIResource id="CabinDescriptor.travelParty.body" />
      </Dialog.Description>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.sailorsCount}>
          <div className={styles.heading}>
            <Sailors className={styles.icon} />
            <label className={styles.label}>Sailors</label>
          </div>

          <div className={styles.control}>
            <span aria-live="polite" className="sr-only">
              {`Current selected number of sailors is ${sailors}`}
            </span>
            <button
              aria-label="Decrease sailors count"
              className={styles.button}
              onClick={onSailorsDecrement}
              type="button"
            >
              <Minus className={styles.icon} />
            </button>
            <span className={styles.value} tabIndex={0}>
              <span className="sr-only">Sailors count is</span>
              {sailors}
            </span>
            <button
              aria-label="Increase sailors count"
              className={styles.button}
              onClick={onSailorsIncrement}
              type="button"
            >
              <Plus className={styles.icon} />
            </button>
          </div>
        </div>

        <label className={styles.accessible}>
          <input
            checked={accessible}
            className={styles.input}
            name="accessible"
            onChange={onIsAccessibleToggle}
            onKeyDown={onAccessibleToggleKeydown}
            type="checkbox"
          />
          Accessible cabins
        </label>

        <Button className={styles.viewCabins} disabled={isViewCabinsButtonDisabled} type="submit" variant="primary">
          {isViewCabinsButtonLoading ? <ThreeDotsLoaderContent /> : 'View Cabins'}
        </Button>
      </form>

      <DisabledSailorPopup
        onDismissClick={onDisabledSailorModalClick}
        onSailorServiceBtnClick={onSailorServiceBtnClick}
        showModal={isDisabledSailorModalOpen}
      />
    </Dialog.Content>
  );
};

export default VerificationModal;
